import { Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { LedgerReopenedBusinessDay } from '@/api/ledger-types'
import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { formatBusinessDate } from '@/lib/datetime'

import { LedgerDualControlNotice, LedgerDualControlOutcome } from './dual-control'
import { useReopenBusinessDay } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'

/**
 * Taking a close back.
 *
 * Offered in exactly one state: the branch has nothing open and its most recent day is the closed
 * one — which is the morning-after shape of "somebody closed the wrong day". The server enforces
 * the same fence ({@code ledger.day_reopen_behind_newer_day}, {@code ledger.day_reopen_period_not_open}),
 * so this button appearing is a convenience, not the authority.
 *
 * Countersigned, and the outcome is read from the envelope: a queued request leaves the day closed
 * and its figures reported, and the panel says so instead of celebrating.
 */
export function ReopenBusinessDayAction({
  branchId,
  businessDate,
}: {
  branchId: string
  businessDate: string
}) {
  const permissions = useLedgerPermissions()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<LedgerReopenedBusinessDay | null>(null)

  const reopen = useReopenBusinessDay()

  useEffect(() => {
    if (!open) {
      setReason('')
      setResult(null)
      reopen.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset() is stable; `reopen` would re-run this on every mutation state change
  }, [open])

  if (!permissions.canReopenDay) {
    return null
  }

  const trimmed = reason.trim()

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Undo2 />
        Reopen {formatBusinessDate(businessDate)}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen {formatBusinessDate(businessDate)}</DialogTitle>
            <DialogDescription>
              Branch {branchId}&apos;s most recent day. Reopening takes back a reported figure — the
              day accepts postings again and its totals stop being final.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            {result === null ? (
              <>
                <LedgerDualControlNotice
                  action="Reopening a closed day"
                  approvePermission={LEDGER_PERMISSIONS.dayReopenApprove}
                  canCountersign={permissions.canCountersignDayReopen}
                />

                <p className="text-xs text-body">
                  This works only while the branch has not traded past it and the accounting period
                  over the date is still open. For anything else, the correction is a fresh entry in
                  the current open day carrying its original value date — the same answer a late
                  field collection gets.
                </p>

                <div className="space-y-1.5">
                  <Label htmlFor="reopen-day-reason">Why is the day being reopened?</Label>
                  <textarea
                    id="reopen-day-reason"
                    rows={3}
                    autoFocus
                    value={reason}
                    placeholder="What was closed in error, and why posting forward cannot correct it."
                    onChange={(event) => setReason(event.target.value)}
                    aria-describedby="reopen-day-reason-hint"
                    className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
                  />
                  <p id="reopen-day-reason-hint" className="text-xs text-muted">
                    Mandatory. It is what the checker reads before agreeing to let a reported figure
                    move, and what an examiner reads afterwards.
                  </p>
                </div>

                {reopen.isError ? <ProblemAlert error={reopen.error} /> : null}
              </>
            ) : (
              <LedgerDualControlOutcome
                result={result}
                awaitingDescription={
                  <>
                    The request to reopen {formatBusinessDate(businessDate)} is stored and waiting
                    for a countersignature. <strong>The day is still closed</strong> and its figures
                    still stand.
                  </>
                }
                appliedDescription={
                  <>
                    {formatBusinessDate(businessDate)} is open again and accepts postings. Close it
                    properly when the correction is in — its figures are not reported until then.
                  </>
                }
              />
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={reopen.isPending}>
              {result === null ? 'Cancel' : 'Close'}
            </Button>
            {result === null ? (
              <Button
                variant="danger"
                disabled={trimmed.length < 12 || reopen.isPending}
                onClick={() => {
                  reopen.mutate(
                    { branchId, businessDate, reason: trimmed },
                    { onSuccess: setResult },
                  )
                }}
              >
                {reopen.isPending ? 'Working…' : 'Reopen the day'}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
